import { assertNotEmpty } from '@festina-life/core'
import { useResizeObserver } from '@festina-life/ui/hooks'
import type { RefObject } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import { useTemplateId } from '../../hooks/useTemplateId'
import { useTemplateHierarchy } from '../../uiModels/versionInfo/useTemplateHierarchy'
import {
  useAllTemplateVersionInfos,
  useTemplateVersionInfo,
} from '../../uiModels/versionInfo/useTemplateVersionInfo'
import { NODE_COLLAPSED_SPACING_VERTICAL } from '../inheritanceHierarchy/constants'
import { useReplacedTemplatesOutsideHierarchy } from '../inheritanceHierarchy/useReplacedTemplatesOutsideHierarchy'
import { VersionedArrowsGroup } from '../versionedTimeline/VersionedArrowsGroup'
import { TemplateVersionsLines } from './TemplateVersionsLines'
import { useTemplatesMetadata } from './useTemplatesMetadata'

type Props = {
  /**
   * Node height to align to.
   */
  nodeHeight: number
  /**
   * Current zoom scale.
   */
  currentScale: number
  /**
   * Outermost container.
   */
  containerRef: RefObject<HTMLDivElement | null>
  /**
   * Left-side containers.
   */
  controlsRef: RefObject<HTMLDivElement | null>
}

export const TemplateVersionsArrows = ({
  nodeHeight,
  currentScale,
  containerRef,
  controlsRef,
}: Props) => {
  const templateId = useTemplateId()
  const { viewAt } = useConfigurationContext()
  const versionInfoForHierarchy = useTemplateVersionInfo({
    id: templateId,
    date: viewAt,
  })
  assertNotEmpty(versionInfoForHierarchy)

  const observedContainer = useResizeObserver<HTMLDivElement>(containerRef)
  const observedControls = useResizeObserver<HTMLDivElement>(controlsRef)

  const hierarchy = useTemplateHierarchy({
    forTemplate: versionInfoForHierarchy,
    withReplacements: false,
  })
  const replacedVersions = useReplacedTemplatesOutsideHierarchy(hierarchy)

  const templateVersions = useAllTemplateVersionInfos(
    hierarchy.map(({ identifier }) => identifier),
  )
  const allVersions =
    templateVersions ||
    (versionInfoForHierarchy ? [versionInfoForHierarchy] : [])

  const {
    earliestDefinedVersionDate: earliestVersionDate,
    latestDefinedVersionDate: latestVersionDate,
  } = useTemplatesMetadata(versionInfoForHierarchy, allVersions)

  const parentWidth = Math.max(
    (observedContainer?.width ?? 0) - Math.ceil(observedControls?.width ?? 0),
    0,
  )

  const replacedVersionsPadding =
    replacedVersions.length * (nodeHeight + NODE_COLLAPSED_SPACING_VERTICAL)
  const arrowsGroupHeight =
    replacedVersionsPadding +
    hierarchy.length * (nodeHeight + NODE_COLLAPSED_SPACING_VERTICAL)

  return (
    <VersionedArrowsGroup
      height={arrowsGroupHeight}
      scale={currentScale}
      parentWidth={parentWidth}
      latestVersionDate={latestVersionDate}
      earliestVersionDate={earliestVersionDate}
      nodeHeight={nodeHeight}
      versions={hierarchy}
      paddingTop={replacedVersionsPadding}
    >
      {TemplateVersionsLines}
    </VersionedArrowsGroup>
  )
}
